import {
    Alert,
    Box,
    Stack,
    Typography,
    styled,
    useMediaQuery,
} from '@mui/material';
import ContractingButton from 'components/profile/ContractingButton';
import LogoUpload from 'components/profile/LogoUpload';
import { NewsletterSignup } from 'components/profile/NewsletterSignup';
import PrimaryBtn from 'components/shared/Buttons/PrimaryButton';
import useForgotPassword from 'hooks/authentication/useForgotPassword';
import useUserSWR from 'hooks/user/useUserSWR';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import SnackbarUtils from 'utils/SnackbarUtils';
import useInvoices from '../../../../hooks/invoices/useInvoices';
import { formatPrice } from '../../../../utils/numberFormatter';
import { isNullOrUndefined } from '../../../../utils/utils';
import { InvoicesInformation } from '../../../invoices/InvoicesInformation';
import { AddressesContent } from '../../../profile/AddressesContent';
import { MyCompany } from '../../../profile/MyCompany';
import { ProfileContent } from '../../../profile/ProfileContent';
import { PageTitle } from '../../../shared/Typography/PageTitle';

const StyledContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    gap: '32px',
    [theme.breakpoints.down('lg')]: {
        gap: '16px',
    },
    [theme.breakpoints.down('md')]: {
        flexDirection: 'column',
    },
}));

const StyledInnerContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    gap: '48px',
    [theme.breakpoints.down('lg')]: {
        gap: '16px',
    },
    [theme.breakpoints.down('md')]: {
        justifyContent: 'space-between',
        paddingBottom: '12px',
    },

    [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        justifyContent: 'unset',
        paddingBottom: '12px',
        gap: '32px',
    },
}));

export const Profile: React.FC = () => {
    const { t } = useTranslation();

    const user = useUserSWR();

    const balance = useInvoices();

    const { balances } = balance.useInvoicesBalanceSWR();

    const small = useMediaQuery('(max-width:1200px)');

    const forgotPassword = useForgotPassword();

    const [disable, setDisable] = useState<boolean>(false);

    return (
        <>
            {!isNullOrUndefined(balances) &&
                !isNullOrUndefined(balances.overDueBalance) &&
                balances.overDueBalance != 0 && (
                    <Alert
                        variant="filled"
                        severity="warning"
                        sx={{ marginBottom: '10px' }}
                    >
                        <Typography gutterBottom>
                            {t('PROFILE.OVER_DUE_BALANCE', {
                                number:
                                    formatPrice(
                                        Number(balances.overDueBalance)
                                    ) + ' Ft',
                            })}
                        </Typography>
                    </Alert>
                )}
            <PageTitle>{t('PROFILE.PAGE_TITLE').toString()}</PageTitle>

            <Box>
                <InvoicesInformation displayType={'PROFILE'} />
            </Box>

            <Stack flexDirection={small ? 'column' : 'row'} gap={2}>
                <Stack gap={2}>
                    <LogoUpload />
                    <ProfileContent />
                    <Stack width="100%" position="relative">
                        <PrimaryBtn
                            sx={{ width: '100%' }}
                            variant="contained"
                            disabled={disable}
                            onClick={() => {
                                forgotPassword
                                    .sendForgotPassword({
                                        email: user?.user?.email,
                                    })
                                    .then(() => {
                                        setDisable(true);
                                        SnackbarUtils.success(
                                            t('NOTIFICATION.PASSWORD_CHANGE')
                                        );
                                    });
                            }}
                        >
                            {t('PROFILE.NEW_PASSWORD')}
                        </PrimaryBtn>
                    </Stack>
                </Stack>

                <Stack flexDirection="column" gap={2}>
                    <Stack flexDirection={small ? 'column' : 'row'} gap={4}>
                        <MyCompany />
                        <NewsletterSignup />
                        <ContractingButton taxNumber={user?.user?.tax_number} />
                    </Stack>

                    <AddressesContent />
                </Stack>
            </Stack>
        </>
    );
};
